// combatpro/companions/companionEngine.js
//
// Combat PRO - Pets & Animal Companions (G268 Pet & Animal Companion Generator).
// Pure helpers for the Player Pets & Animal Companions modal and the DM's combat
// selection surface. The persistent animal is owned by the animal programme
// (G268, later enriched by G269 and G270); Combat PRO only reads it and, for an
// animal the server has admitted, adds a combatant built from the bounded combat
// snapshot. Nothing here edits growth, training schedules, care, habitat or
// equipment. The one choice a player makes here is a trainer from the shortlist
// the Animal Training & Trainer Generator saved (G270 section 19); combat itself
// never browses or books trainers. No optional chaining. No nullish coalescing.
// No em dash.

export var TRAINING_OWNER = 'Animal Training & Trainer Generator';
export var CARE_OWNER = 'Animal Equipment, Care & Habitat Generator';

// The modal's sections. Training is the Animal Training & Trainer Generator (G270) group: its nested surfaces show
// what G270 saved onto the canonical record, and Find Trainer / Trainer Details let the player browse the saved
// trainer shortlist and choose one (scheduling, progress and completion stay in the generator). Equipment, Care and
// Habitat are G269 (Animal Equipment, Care & Habitat Generator)
// surfaces nested under their group; they show what G269 saved onto the canonical record. Changes are made in the
// generators; Combat PRO never manages equipment, care, habitat or a stable.
export var MODAL_SECTIONS = [
  { id: 'animals', label: 'My Animals', available: true },
  { id: 'profile', label: 'Animal Profile', available: true },
  { id: 'growth', label: 'Growth & Development', available: true },
  { id: 'roles', label: 'Roles', available: true },
  { id: 'training', label: 'Training', available: true, owner: TRAINING_OWNER, group: true },
  { id: 'training_overview', label: 'Training Overview', available: true, owner: TRAINING_OWNER, parent: 'training' },
  { id: 'training_available', label: 'Available Training', available: true, owner: TRAINING_OWNER, parent: 'training' },
  { id: 'training_current', label: 'Current Training', available: true, owner: TRAINING_OWNER, parent: 'training' },
  { id: 'trainer', label: 'Find Trainer', available: true, owner: TRAINING_OWNER, parent: 'training' },
  { id: 'trainer_details', label: 'Trainer Details', available: true, owner: TRAINING_OWNER, parent: 'training' },
  { id: 'training_schedule', label: 'Schedule Training', available: true, owner: TRAINING_OWNER, parent: 'training' },
  { id: 'training_progress', label: 'Training Progress', available: true, owner: TRAINING_OWNER, parent: 'training' },
  { id: 'training_completed', label: 'Completed Training', available: true, owner: TRAINING_OWNER, parent: 'training' },
  { id: 'training_commands', label: 'Commands & Skills', available: true, owner: TRAINING_OWNER, parent: 'training' },
  { id: 'training_history', label: 'Training History', available: true, owner: TRAINING_OWNER, parent: 'training' },
  { id: 'equipment', label: 'Equipment', available: true, owner: CARE_OWNER, group: true },
  { id: 'outfit', label: 'Outfit Animal', available: true, owner: CARE_OWNER, parent: 'equipment' },
  { id: 'gear', label: 'Current Gear', available: true, owner: CARE_OWNER, parent: 'equipment' },
  { id: 'fit', label: 'Fit & Growth', available: true, owner: CARE_OWNER, parent: 'equipment' },
  { id: 'equipment_history', label: 'Equipment History', available: true, owner: CARE_OWNER, parent: 'equipment' },
  { id: 'care', label: 'Care', available: true, owner: CARE_OWNER, group: true },
  { id: 'supplies', label: 'Supplies', available: true, owner: CARE_OWNER, parent: 'care' },
  { id: 'travel', label: 'Travel Preparation', available: true, owner: CARE_OWNER, parent: 'care' },
  { id: 'habitat', label: 'Habitat', available: true, owner: CARE_OWNER },
  { id: 'combat', label: 'Combat Readiness', available: true },
  { id: 'share', label: 'Information Sharing', available: true },
  { id: 'history', label: 'History', available: true },
  { id: 'select', label: 'Bring Into Combat (DM)', available: true }
];

export var G269_SECTIONS = ['equipment', 'outfit', 'gear', 'fit', 'equipment_history', 'care', 'supplies', 'travel', 'habitat'];

export var G270_SECTIONS = ['training', 'training_overview', 'training_available', 'training_current', 'trainer', 'trainer_details', 'training_schedule', 'training_progress', 'training_completed', 'training_commands', 'training_history'];

function g270Of(profile) { return (profile && profile.g270 && typeof profile.g270 === 'object') ? profile.g270 : {}; }

function arr(v) { return Array.isArray(v) ? v : []; }

// What G270 saved onto a companion record (profile.g270.summary, written by the generator's engine), for the training
// surfaces. An animal G270 has not managed yet still shows what the canonical record holds.
export function trainingView(record, profile, entry) {
  var r = record || {};
  var training = r.training || {};
  var history = r.history || {};
  var g = g270Of(profile);
  var s = (g.summary && typeof g.summary === 'object') ? g.summary : null;
  var row = (entry && entry.summary) ? entry.summary : {};
  var current = training.current_programme;
  return {
    managed: !!s,
    as_of: s ? s.as_of : '',
    level: labelOf((s && s.training_level) || training.training_level || 'untrained'),
    completed: s ? arr(s.completed) : arr(training.completed).map(function (t) { return { label: labelOf(t.programme_id), date: t.completed_date, level: t.level, family: '', trainer: '' }; }),
    current: s ? arr(s.current) : (current && current.programme_id ? [{ label: labelOf(current.programme_id), status_label: 'Active', start_date: current.start_date, expected_completion: current.expected_completion, progress_percent: Math.round((Number(current.progress) || 0) * 100), trainer: '' }] : []),
    schedule: s ? arr(s.schedule) : [],
    ended: s ? arr(s.ended) : [],
    available: s ? arr(s.available) : [],
    commands: s ? arr(s.commands_by_family) : [],
    custom_commands: s ? arr(s.custom_commands) : arr(training.custom_commands),
    limitations: s ? arr(s.command_limitations) : [],
    roles: s ? arr(s.role_readiness) : [],
    shortlist: s ? arr(s.shortlist) : [],
    shortlist_programme: s ? (s.shortlist_programme || '') : '',
    shortlist_place: s ? labelOf(s.shortlist_place) : '',
    trainer_history: s ? arr(s.trainer_history) : arr(history.trainer_history),
    training_history: (s ? arr(s.training_history) : arr(history.training_history)).map(labelOf),
    costs_paid_gp: s ? (Number(s.costs_paid_gp) || 0) : 0,
    choice: (entry && entry.trainer_choice) ? entry.trainer_choice : null,
    state: row.training_state || null
  };
}

var TIER_ORDER = { novice: 0, competent: 1, expert: 2, master: 3, legendary: 4 };

// Browse the saved trainer shortlist: filters { bookable, local, tier } and a sort ('soonest', 'reliability',
// 'reputation' or 'tier'). Pure, so the browsing never needs the engine and never books anything.
export function filterTrainers(list, filters, sort) {
  var f = filters || {};
  var out = arr(list).filter(function (t) {
    if (f.bookable && !t.bookable) { return false; }
    if (f.local && !t.local) { return false; }
    if (f.tier && t.tier !== f.tier) { return false; }
    return true;
  });
  var key = sort || 'soonest';
  return out.slice().sort(function (a, b) {
    if (key === 'reliability') { return (Number(b.reliability_percent) || 0) - (Number(a.reliability_percent) || 0); }
    if (key === 'reputation') { return (Number(b.reputation) || 0) - (Number(a.reputation) || 0); }
    if (key === 'tier') { return (TIER_ORDER[b.tier] || 0) - (TIER_ORDER[a.tier] || 0); }
    if (a.bookable !== b.bookable) { return a.bookable ? -1 : 1; }
    return String(a.next_available_date || '').localeCompare(String(b.next_available_date || ''));
  });
}

// One line for the training state Combat PRO shows (eligible, ineligible with reason, in training, awaiting maturity,
// missing required combat training).
export function trainingStateLine(state) {
  if (!state || !state.status) { return ''; }
  var reasons = arr(state.reasons).join('; ');
  var cur = state.current;
  if (state.status === 'in_training' && cur) {
    return (state.label || 'In training') + ': ' + cur.programme + (cur.trainer ? ' with ' + cur.trainer : '') + (cur.expected_completion ? ', expected ' + cur.expected_completion : '');
  }
  if (state.status === 'eligible') { return state.label || 'Eligible'; }
  var missing = state.combat_training && arr(state.combat_training.missing).length ? ' (needs ' + state.combat_training.missing.join(', ') + ')' : '';
  return (state.label || labelOf(state.status)) + missing + (reasons ? ': ' + reasons : '');
}

function g269Of(profile) { return (profile && profile.g269 && typeof profile.g269 === 'object') ? profile.g269 : {}; }

function itemLabel(id, g) {
  var items = g.items || {};
  if (items[id] && items[id].label) { return items[id].label; }
  var custom = (g.custom_equipment || []).filter(function (c) { return c.id === id; })[0];
  if (custom) { return custom.label; }
  return labelOf(id);
}

// What G269 has saved onto a companion record, for the read-only Equipment, Care and Habitat surfaces.
export function equipmentView(record, profile) {
  var r = record || {};
  var eq = r.equipment || {};
  var g = g269Of(profile);
  var items = g.items || {};
  var worn = (eq.equipped || []).map(function (e) {
    var d = items[e.item_id] || {};
    return { label: itemLabel(e.item_id, g), fit_status: e.fit_status, fit_label: labelOf(e.fit_status), condition: labelOf(d.condition || 'good'), quality: labelOf(d.quality || 'standard'), slot: eq.saddle_harness === e.item_id ? 'saddle or harness' : (eq.barding_armour === e.item_id ? 'barding or armour' : '') };
  });
  var stored = (eq.stored || []).map(function (id) { var d = items[id] || {}; return { label: itemLabel(id, g), condition: labelOf(d.condition || 'good') }; });
  var care = r.care || {};
  return {
    managed: !!g.schema,
    worn: worn,
    stored: stored,
    outfit: g.outfit || null,
    fit: g.fit_summary || [],
    history: (r.history && r.history.equipment_history) ? r.history.equipment_history : [],
    repairs: g.repair_history || [], resizes: g.resize_history || [], replacements: g.replacement_history || [],
    care: { profile: labelOf(care.care_profile), feeding: care.feeding ? care.feeding.plan : '', grooming: care.grooming, exercise: care.exercise, housing: care.housing, travel: care.travel_needs, state: labelOf(care.current_care_state) },
    care_plan: g.care_plan || null,
    supplies: g.supply_plan || null,
    habitat: g.habitat || null,
    habitat_id: care.habitat || '',
    travel: g.travel_plan || null
  };
}

export function sectionById(id) {
  for (var i = 0; i < MODAL_SECTIONS.length; i++) {
    if (MODAL_SECTIONS[i].id === id) { return MODAL_SECTIONS[i]; }
  }
  return MODAL_SECTIONS[0];
}

export function labelOf(id) {
  if (id === null || id === undefined || id === '') { return ''; }
  var text = String(id).replace(/^(tr|eq|acr|acf|acc)_/, '').replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// Current Combat PRO player names, trimmed and de-duplicated (case-insensitive).
export function ownerNames(players) {
  var seen = {};
  var out = [];
  var list = players || [];
  for (var i = 0; i < list.length; i++) {
    var p = list[i];
    if (!p || p.companion) { continue; }
    var name = p.name ? String(p.name).trim() : '';
    var key = name.toLowerCase();
    if (!name || seen[key]) { continue; }
    seen[key] = true;
    out.push(name);
  }
  return out;
}

function summaryOf(entry) { return (entry && entry.summary) ? entry.summary : {}; }

// The account's own animals grouped by owner character. Characters that are in
// the current Combat PRO party come first; animals never cross characters.
export function groupByOwner(records, players) {
  var party = ownerNames(players).map(function (n) { return n.toLowerCase(); });
  var groups = {};
  var order = [];
  (records || []).forEach(function (entry) {
    var s = summaryOf(entry);
    if (s.archived) { return; }
    var name = s.owner_character_name || 'Unassigned';
    var key = String(s.owner_character || name);
    if (!groups[key]) {
      groups[key] = { owner_character: key, owner_character_name: name, in_party: party.indexOf(String(name).toLowerCase()) >= 0, animals: [] };
      order.push(key);
    }
    groups[key].animals.push(entry);
  });
  return order.map(function (k) { return groups[k]; }).sort(function (a, b) {
    if (a.in_party !== b.in_party) { return a.in_party ? -1 : 1; }
    return String(a.owner_character_name).localeCompare(String(b.owner_character_name));
  });
}

export function inCombat(players, companionId) {
  var list = players || [];
  for (var i = 0; i < list.length; i++) {
    if (list[i] && list[i].companion && list[i].companion.companion_id === companionId) { return true; }
  }
  return false;
}

function boundedEffect(value) {
  var raw = value && typeof value === 'object' ? value : {};
  var out = {};
  ['damage', 'healing', 'condition', 'triggered_effect'].forEach(function (key) {
    if (raw[key] !== undefined && raw[key] !== null) { out[key] = raw[key]; }
  });
  return out;
}

// Action records are projections of the admitted G268/G270 snapshot only. Combat
// PRO never derives commands from labels, roles, care or habitat data.
export function boundedCompanionActions(combat) {
  var source = combat || {};
  var actions = [];
  arr(source.attacks).forEach(function (attack, index) {
    if (!attack || !attack.id || !attack.label) { return; }
    actions.push({
      id: 'attack:' + attack.id, source_id: attack.id, label: attack.label,
      kind: 'attack', requires_target: true,
      attack_bonus: typeof attack.attack_bonus_guidance === 'number' ? attack.attack_bonus_guidance : null,
      effect: boundedEffect({ damage: attack.damage_guidance }),
      max_uses: typeof attack.max_uses === 'number' ? attack.max_uses : null,
      source_index: index
    });
  });
  arr(source.trained_actions).forEach(function (trained, index) {
    if (!trained || !trained.id || !trained.label) { return; }
    actions.push({
      id: 'trained:' + trained.id, source_id: trained.id, label: trained.label,
      kind: 'trained', requires_target: trained.requires_target === true,
      effect: boundedEffect(trained.effect),
      max_uses: typeof trained.max_uses === 'number' ? trained.max_uses : null,
      source_index: index
    });
  });
  return actions;
}

export function companionPlayerProjection(summary) {
  var row = summary || {};
  return {
    display_cards: [{
      title: row.name || 'Companion',
      summary: [row.species, row.age, row.growth_stage_label, labelOf(row.primary_role)].filter(function (v) { return v; }).join(' · ')
    }],
    player_safe_facts: [
      'Owner: ' + (row.owner_character_name || 'Unassigned'),
      'Readiness: ' + labelOf(row.readiness || 'unknown'),
      'Combat eligibility: ' + (row.eligible ? 'Eligible' : 'Not eligible'),
      row.training_summary ? 'Training: ' + row.training_summary : '',
      row.equipment_summary ? 'Equipment: ' + row.equipment_summary : ''
    ].filter(function (value) { return value; }),
    companion_state: {
      companion_id: row.companion_id, eligible: row.eligible === true,
      readiness: row.readiness || null, availability: row.availability || null,
      combat_state: row.combat_state || null
    }
  };
}

// Short readable reasons for an ineligible animal (server labels first).
export function reasonText(row) {
  var reasons = (row && row.reasons) ? row.reasons : [];
  if (!reasons.length) { return ''; }
  return reasons.map(function (r) { return r.label || labelOf(r.code); }).join('; ');
}

function attackLine(a) {
  var bonus = (typeof a.attack_bonus_guidance === 'number') ? (a.attack_bonus_guidance >= 0 ? ' +' + a.attack_bonus_guidance : ' ' + a.attack_bonus_guidance) : '';
  var dmg = a.damage_guidance ? ' (' + a.damage_guidance + ')' : '';
  return (a.label || labelOf(a.id)) + bonus + dmg;
}

function trainingNote(view) {
  var t = view && view.training_state ? view.training_state : null;
  if (!t || !t.status) { return ''; }
  var actions = arr(t.trained_combat_actions);
  var limits = arr(t.command_limitations);
  return 'Training: ' + trainingStateLine(t) + (actions.length ? '. Trained combat actions: ' + actions.join(', ') : '') + (limits.length ? '. Untrained commands: ' + limits.join(', ') : '');
}

// A Combat PRO combatant for an animal the server admitted (POST combat-entry 200).
// Returns null when the payload has no combat block, so an unadmitted animal can
// never be turned into a combatant.
export function companionCombatant(payload) {
  var c = payload && payload.combatant ? payload.combatant : null;
  if (!c || !c.combat || !c.companion_id) { return null; }
  var combat = c.combat;
  var durability = combat.durability || {};
  var defences = combat.defences || {};
  var hp = typeof durability.hit_points_guidance === 'number' ? durability.hit_points_guidance : 1;
  var ac = typeof defences.armour_class_guidance === 'number' ? defences.armour_class_guidance : 10;
  var moves = (combat.movement_modes || []).map(function (m) { return m.mode + ' ' + m.feet + ' ft'; }).join(', ');
  var notes = [
    (c.species || 'Animal') + ' owned by ' + (c.owner_character_name || 'its owner') + '. ' + (c.combat_state_label || ''),
    'Attacks: ' + ((combat.attacks || []).map(attackLine).join(', ') || 'none'),
    'Movement: ' + (moves || 'none'),
    (combat.trained_actions || []).length ? 'Trained actions: ' + combat.trained_actions.map(function (t) { return t.label || labelOf(t.id); }).join(', ') : '',
    combat.behaviour_morale_rules ? 'Morale: ' + combat.behaviour_morale_rules : '',
    combat.retreat_behaviour ? 'Retreat: ' + combat.retreat_behaviour : '',
    trainingNote(c.list_view),
    combat.equipment_effects && (combat.equipment_effects.items || []).length ? 'Equipment: ' + combat.equipment_effects.items.map(function (i) { return i.label + ' (' + (i.effects || []).join(', ') + ')'; }).join('; ') : '',
    combat.equipment_effects && (combat.equipment_effects.reasons || []).length ? 'Equipment limits: ' + combat.equipment_effects.reasons.join(' ') : ''
  ].filter(function (line) { return line; }).join('\n');
  var actions = boundedCompanionActions(combat);
  var actionUses = {};
  actions.forEach(function (action) { if (action.max_uses !== null) { actionUses[action.id] = action.max_uses; } });
  return {
    name: c.name + ' (' + (c.owner_character_name || 'companion') + ')',
    hp: hp,
    maxHp: hp,
    ac: ac,
    initiative: 0,
    notes: notes,
    companion: {
      companion_id: c.companion_id,
      owner_character: c.owner_character,
      owner_character_name: c.owner_character_name,
      combat_state: c.combat_state,
      combat_ref: combat.combat_ref || null,
      snapshot_version: c.snapshot_version,
      actions: actions,
      live: {
        active: true, withdrawn: false, participation: 'ACTIVE', target: null,
        condition: [], last_action: null, action_uses: actionUses
      }
    }
  };
}

// Read-only facts already recorded on the canonical record for a G269 / G270 section.
export function recordedFor(sectionId, record) {
  var r = record || {};
  if (sectionId === 'training' || sectionId === 'trainer') {
    var training = r.training || {};
    var done = (training.completed || []).map(function (t) { return labelOf(t.programme_id) + (t.completed_date ? ' (' + t.completed_date + ')' : ''); });
    var trainers = (r.history && r.history.trainer_history) ? r.history.trainer_history : [];
    return sectionId === 'training' ? done : trainers;
  }
  if (sectionId === 'equipment') {
    var eq = r.equipment || {};
    return (eq.equipped || []).map(function (e) { return labelOf(e.item_id) + ' (' + labelOf(e.fit_status) + ')'; })
      .concat((eq.stored || []).map(function (id) { return labelOf(id) + ' (stored)'; }));
  }
  if (sectionId === 'care') {
    var care = r.care || {};
    var feeding = care.feeding && care.feeding.plan ? care.feeding.plan : '';
    return [care.current_care_state ? 'Care state: ' + labelOf(care.current_care_state) : '', feeding ? 'Feeding: ' + feeding : '', care.exercise ? 'Exercise: ' + care.exercise : '', care.grooming ? 'Grooming: ' + care.grooming : ''].filter(function (x) { return x; });
  }
  if (sectionId === 'habitat') {
    var c2 = r.care || {};
    return [c2.habitat ? 'Habitat: ' + labelOf(c2.habitat) : '', c2.housing ? 'Housing: ' + labelOf(c2.housing) : '', c2.travel_needs ? 'Travel: ' + c2.travel_needs : ''].filter(function (x) { return x; });
  }
  return [];
}
